// @owners { team: patients }
import { type AltoIconName, BORDERS, SIZES } from '@alto/design-library-tokens';
import { ListButton, type ListButtonProps, Row } from '@alto/design-system';
import React, { useRef } from 'react';
import styled from 'styled-components';
import styledNative from 'styled-components/native';

const HiddenFileInputWrapper = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  whitespace: nowrap;
  wordwrap: normal;
`;

const BottomAttachedImage = styled.img`
  height: auto;
  max-height: ${SIZES.ILLUSTRATION.XXL.px};
  width: 100%;
  object-fit: contain;
  border-top-left-radius: ${BORDERS.RADIUS.LG.px};
  border-top-right-radius: ${BORDERS.RADIUS.LG.px};
`;

const Image = styled.img`
  height: auto;
  max-height: ${SIZES.ILLUSTRATION.XXL.px};
  width: 100%;
  object-fit: contain;
  border-radius: ${BORDERS.RADIUS.LG.px};
`;

const TopAttachedListButton = styledNative(ListButton)`
  border-top-left-radius: ${BORDERS.RADIUS.NONE.px};
  border-top-right-radius: ${BORDERS.RADIUS.NONE.px};
  width: 100%;
`;

const FullWidthListButton = styledNative(ListButton)`
  width: 100%;
`;

export type ImageUploadListButtonProps = {
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly name: string;
  readonly onUpload: (file: File) => void;
  readonly onDelete?: () => void; // When onDelete is undefined, the delete button will not be shown, we will use a external component to delete the image
  readonly source: string;
  readonly children: string | string[];
  readonly LeftContent?: ListButtonProps['LeftContent'];
  readonly rightIcon?: AltoIconName;
};

export const ImageUploadListButton = ({
  disabled,
  loading,
  name,
  onUpload,
  onDelete,
  source,
  children,
  LeftContent,
  rightIcon = 'camera',
}: ImageUploadListButtonProps) => {
  const fileInput = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInput?.current?.click();
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.files && event.target.files.length > 0) {
      onUpload(event.target.files[0]);
    }
  };

  // If image source is provided and onDelete is not defined, we will only show the image, without the delete button
  if (source && !onDelete) {
    return (
      <>
        <Row
          flexGrow={1}
          center
        >
          <Image
            src={source}
            alt={name}
          />
        </Row>
      </>
    );
  }

  return (
    <Row>
      {source ? (
        <>
          <Row
            flexGrow={1}
            center
          >
            <BottomAttachedImage
              src={source}
              alt={name}
            />
          </Row>
          <TopAttachedListButton
            label={children}
            onPress={handleDeleteClick}
            rightIcon="trash"
            disabled={disabled || loading}
            loading={loading}
            loadingLabel="Uploading..."
          />
        </>
      ) : (
        <>
          <FullWidthListButton
            onPress={handleUploadClick}
            label={children}
            rightIcon={rightIcon}
            disabled={disabled || loading}
            loading={loading}
            LeftContent={LeftContent}
            loadingLabel="Uploading..."
          />
          <HiddenFileInputWrapper
            data-testid={name}
            id={name}
            name={name}
            onChange={handleFileChange}
            ref={fileInput}
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            multiple={false}
          />
        </>
      )}
    </Row>
  );
};
